import React, { useEffect, useState } from 'react';
import api from '../../api/axios';
import ConfirmationModal from '../../components/ConfirmationModal';
import { apiErrorMessage, formatSupportExpiry } from '../../services/supportDataService';

interface SupportStaffMember {
  userId: number;
  name: string;
  email: string;
  addedBy: string;
  addedAt: string;
  revokedBy: string | null;
  revokedAt: string | null;
  notes: string | null;
}

/**
 * System Admin → Support staff (tdp-api docs/support-access-plan.md §4).
 * Who MAY do support. Being listed grants nothing on its own: entering an
 * organisation also needs that organisation's Admin to approve a grant.
 */
const SupportStaff: React.FC = () => {
  const [staff, setStaff] = useState<SupportStaffMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [notes, setNotes] = useState('');
  const [needsName, setNeedsName] = useState(false);
  const [busy, setBusy] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [formNotice, setFormNotice] = useState<string | null>(null);
  const [revoking, setRevoking] = useState<SupportStaffMember | null>(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.get('/system-admin/support-staff');
      setStaff(response.data?.staff || []);
    } catch (err) {
      setError(apiErrorMessage(err, 'Failed to load support staff.'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { void load(); }, []);

  const add = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setFormError(null);
    setFormNotice(null);
    try {
      const added = email.trim();
      const response = await api.post('/system-admin/support-staff', {
        email: added,
        name: name.trim() || undefined,
        notes: notes.trim() || undefined,
      });
      // The API creates the sign-in when the address has none (Cognito then
      // emails a temporary password), or links an existing one untouched.
      setFormNotice(response.data?.invited
        ? `Added. ${added} has been sent an invitation with a temporary password.`
        : `Added. ${added} already had a sign-in, which is now a support account; their password is unchanged.`);
      setEmail(''); setName(''); setNotes(''); setNeedsName(false);
      await load();
    } catch (err: any) {
      if (err?.response?.data?.code === 'NAME_REQUIRED') setNeedsName(true);
      setFormError(apiErrorMessage(err, 'Could not add support staff.'));
    } finally {
      setBusy(false);
    }
  };

  const revoke = async () => {
    if (!revoking) return;
    setBusy(true);
    try {
      await api.delete(`/system-admin/support-staff/${revoking.userId}`);
      setRevoking(null);
      await load();
    } catch (err) {
      setRevoking(null);
      setError(apiErrorMessage(err, 'Could not revoke support access.'));
    } finally {
      setBusy(false);
    }
  };

  const active = staff.filter(s => !s.revokedAt);

  return (
    <div className="sys-page">
      <div className="sys-page__head">
        <p className="sys-page__eyebrow">Platform</p>
        <h1 className="sys-page__title">Support staff</h1>
        <span className="sys-page__count">{loading ? 'Loading…' : `${active.length} active`}</span>
      </div>

      <div className="sys-page__body">
        <div className="ivory-panel sys-panel sys-panel--pad mb-3">
          <p className="sys-table__muted mb-3">
            People who may enter an organisation for support, once that organisation's admin approves.
            Use a dedicated address that belongs to no organisation, e.g. <code>name+support@rustleit.com</code>.
            If it has no sign-in yet, one is created and an invitation with a temporary password is emailed.
          </p>
          {formError && <div className="alert alert-danger py-2">{formError}</div>}
          {formNotice && <div className="alert alert-success py-2">{formNotice}</div>}
          <form className="row g-2 align-items-end" onSubmit={add}>
            <div className="col-md-4">
              <label className="form-label" htmlFor="ss-email">Email</label>
              <input id="ss-email" type="email" className="form-control" value={email} required disabled={busy}
                onChange={e => { setEmail(e.target.value); setNeedsName(false); }} />
            </div>
            {needsName && (
              <div className="col-md-3">
                <label className="form-label" htmlFor="ss-name">Name</label>
                <input id="ss-name" className="form-control" value={name} required disabled={busy}
                  onChange={e => setName(e.target.value)} placeholder="e.g. Jonny Mills (Support)" />
              </div>
            )}
            <div className="col-md-3">
              <label className="form-label" htmlFor="ss-notes">Notes (optional)</label>
              <input id="ss-notes" className="form-control" value={notes} maxLength={255} disabled={busy}
                onChange={e => setNotes(e.target.value)} />
            </div>
            <div className="col-md-2">
              <button type="submit" className="btn btn-primary w-100" disabled={busy || !email.trim()}>Add</button>
            </div>
          </form>
        </div>

        {error ? (
          <div className="alert alert-danger">{error}</div>
        ) : loading && staff.length === 0 ? (
          <div className="d-flex justify-content-center py-5">
            <div className="spinner-border text-success" role="status"><span className="visually-hidden">Loading…</span></div>
          </div>
        ) : staff.length === 0 ? (
          <div className="ivory-panel sys-panel sys-panel--pad text-center py-5">
            <div className="sys-page__title mb-2">No support staff yet</div>
            <div className="sys-table__muted">Add a support login above.</div>
          </div>
        ) : (
          <div className="ivory-panel sys-panel sys-panel--table">
            <div className="sys-table-scroll">
              <table className="sys-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Added</th>
                    <th>Status</th>
                    <th>Notes</th>
                    <th aria-label="Actions" />
                  </tr>
                </thead>
                <tbody>
                  {staff.map(s => (
                    <tr key={s.userId}>
                      <td className="sys-table__name">{s.name}</td>
                      <td>{s.email}</td>
                      <td className="sys-table__muted" style={{ whiteSpace: 'nowrap' }}>
                        {formatSupportExpiry(s.addedAt)} by {s.addedBy}
                      </td>
                      <td style={{ fontSize: '0.83rem' }}>
                        {s.revokedAt ? (
                          <span className="sys-state sys-state--off" title={`Revoked by ${s.revokedBy}`}>
                            <i className="fas fa-minus-circle" />Revoked {formatSupportExpiry(s.revokedAt)}
                          </span>
                        ) : (
                          <span className="sys-state sys-state--on"><i className="fas fa-check-circle" />Active</span>
                        )}
                      </td>
                      <td className="sys-table__muted">{s.notes || '—'}</td>
                      <td>
                        {!s.revokedAt && (
                          <button type="button" className="btn btn-sm btn-outline-danger" disabled={busy} onClick={() => setRevoking(s)}>
                            Revoke
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      <ConfirmationModal
        show={!!revoking}
        title="Revoke support access?"
        message={`${revoking?.name ?? ''} will no longer be able to enter any organisation, even one with an active grant. History is kept.`}
        confirmText="Revoke"
        isDeleting={busy}
        onCancel={() => setRevoking(null)}
        onConfirm={() => { void revoke(); }}
      />
    </div>
  );
};

export default SupportStaff;
